import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, Clock, CalendarCheck, ReceiptText, Wallet, Anchor,
  LogOut, Menu, X, Shield, Building2, ChartBar, User, FileText,
} from "lucide-react";

const NAV = {
  super_admin: [
    { to: "/super-admin",            label: "Dashboard",  icon: LayoutDashboard, end: true, testid: "nav-sa-dashboard" },
    { to: "/super-admin/companies",  label: "Companies",  icon: Building2,                  testid: "nav-sa-companies" },
    { to: "/super-admin/reports",    label: "Reports",    icon: ChartBar,                   testid: "nav-sa-reports" },
  ],
  admin: [
    { to: "/",            label: "Dashboard",  icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
    { to: "/employees",   label: "Employees",  icon: Users,                      testid: "nav-employees" },
    { to: "/attendance",  label: "Attendance", icon: Clock,                      testid: "nav-attendance" },
    { to: "/leaves",      label: "Leaves",     icon: CalendarCheck,              testid: "nav-leaves" },
    { to: "/payroll",     label: "Payroll",    icon: Wallet,                     testid: "nav-payroll" },
    { to: "/invoices",    label: "Invoices",   icon: ReceiptText,                testid: "nav-invoices" },
  ],
  employee: [
    { to: "/me",            label: "Dashboard",   icon: LayoutDashboard, end: true, testid: "nav-me" },
    { to: "/me/profile",    label: "My Profile",  icon: User,                       testid: "nav-me-profile" },
    { to: "/me/attendance", label: "Attendance",  icon: Clock,                      testid: "nav-me-attendance" },
    { to: "/me/leaves",     label: "Leaves",      icon: CalendarCheck,              testid: "nav-me-leaves" },
    { to: "/me/payslips",   label: "Payslips",    icon: FileText,                   testid: "nav-me-payslips" },
  ],
};
NAV.hr = NAV.admin; 

const BRAND = {
  super_admin: { name: "PLATFORM", sublabel: "SUPER ADMIN" },
  admin:       { name: "OCEANFREIGHT", sublabel: "HRMS · v2.0" },
  hr:          { name: "OCEANFREIGHT", sublabel: "HRMS · v2.0" },
  employee:    { name: "OCEANFREIGHT", sublabel: "EMPLOYEE PORTAL" },
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const role = user?.role || "employee";
  const nav = NAV[role] || [];
  const brand = BRAND[role] || BRAND.employee;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const dateStr = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

  return (
    <div className="min-h-screen flex bg-[#F4F4F5]">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-[#09090B] text-zinc-100 flex flex-col transform transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 lg:static`}
        data-testid="sidebar"
      >
        <div className="px-6 py-6 border-b border-zinc-800 flex items-center justify-between">
          <Link to={nav[0]?.to || "/"} className="flex items-center gap-2 flex-1 min-w-0" data-testid="brand-link">

            {/* Conditional Logo/Icon Rendering */}
            {role === "super_admin" ? (
              <span className="h-9 w-9 grid place-items-center rounded-sm bg-zinc-100">
                <Shield className="h-5 w-5 text-black" strokeWidth={2.5} />
              </span>
            ) : user?.logo_url ? (
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${user?.logo_url}`}
                alt={user.company_name || "Company Logo"}
                className="h-12 w-12 rounded-sm object-contain bg-white p-1"
              />
            ) : (
              <span className="h-9 w-9 grid place-items-center rounded-sm bg-[#F97316]">
                <Anchor className="h-5 w-5 text-black" strokeWidth={2.5} />
            </span>
            )}

            {/* Brand Names */}
            <div className="leading-tight">
              <div className="font-heading font-black text-base tracking-tight">{user?.company_name || brand.name}</div>
              <div className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">{brand.sublabel}</div>
            </div>
          </Link>

          {/* Mobile Close Button */}
          <button className="lg:hidden ml-auto flex items-center justify-center hover:bg-zinc-800 text-zinc-400" onClick={() => setOpen(false)} data-testid="sidebar-close">
            <X className="h-5 w-5 stroke-[3]" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1">
          <div className="px-3 mb-3 text-[10px] tracking-widest font-mono uppercase text-zinc-600">
            {role === "super_admin" ? "Platform" : role === "employee" ? "My space" : "Workspace"}
          </div>
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `sidebar-link flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm border-l-4 ${
                    isActive
                      ? "bg-zinc-900 text-white border-[#F97316]"
                      : "border-transparent text-zinc-400 hover:bg-zinc-900 hover:text-white"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="px-3 py-4 border-t border-zinc-800">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-zinc-400 truncate" data-testid="user-name">{user?.name}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600" data-testid="user-role">
              {user?.role?.replace("_", " ")}
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="btn-pressable w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-300 hover:bg-zinc-900 rounded-sm uppercase tracking-widest font-mono"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {open && <div className="lg:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setOpen(false)} />}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 bg-white border-b border-zinc-200">
          <div className="flex items-center justify-between px-4 lg:px-8 py-3">
            <div className="flex items-center gap-3">
              <button className="lg:hidden p-2 -ml-2" onClick={() => setOpen(true)} data-testid="sidebar-open">
                <Menu className="h-5 w-5" />
              </button>
              <div className="font-mono text-[11px] tracking-widest text-zinc-500 uppercase">{dateStr}</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden md:block text-right">
                <div className="text-xs font-medium text-zinc-900" data-testid="topbar-user-name">{user?.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{user?.email}</div>
              </div>
              <div className={`h-9 w-9 grid place-items-center text-white text-sm font-heading font-bold rounded-sm ${
                role === "super_admin" ? "bg-[#F97316]" : "bg-[#09090B]"
              }`}>
                {(user?.name || "U").slice(0, 1).toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6 lg:p-10" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}